import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription, finalize } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { BarcodeScannerApiService } from './barcode-scanner-api.service';
import { BarcodeScannerSessionService } from './barcode-scanner-session.service';
import { BarcodeScannerSoundService } from './barcode-scanner-sound.service';
import {
  DAMAGE_RESOLUTIONS,
  DamageResolution,
  EMPTY_SCANNER_SESSION,
  PalletRecord,
  PoolClient,
  ProductRecord,
  ScanEvent,
  ScanLogEntry,
  ScanOutcome,
  ScannerAdapterStatus,
  ScannerMode,
  ScannerSessionState,
  StoreOption,
  TripLookup,
} from './barcode-scanner.models';
import { ScannerInputService } from './scanner-input.service';

@Component({
  selector: 'app-barcode-scanner',
  templateUrl: './barcode-scanner.component.html',
  styleUrls: ['./barcode-scanner.component.scss'],
})
export class BarcodeScannerComponent implements OnInit, OnDestroy {
  readonly damageResolutions = DAMAGE_RESOLUTIONS;
  readonly modeLabels: Record<ScannerMode, string> = {
    setup: 'Setup',
    'asn-detect': 'ASN Detect',
    'trip-audit': 'Trip Audit',
    'blind-trip-audit': 'Blind Trip Audit',
    'store-audit': 'Store Audit',
    'pallet-list': 'Pallet List',
    'pallet-products': 'Pallet Products',
    'barcode-list': 'Barcode List',
  };

  session: ScannerSessionState = { ...EMPTY_SCANNER_SESSION };
  statuses: ScannerAdapterStatus[] = [];
  mode: ScannerMode = 'setup';
  loading = false;
  loadingLabel = '';
  processingBarcodes = new Set<string>();

  poolClients: PoolClient[] = [];
  storeOptions: StoreOption[] = [];
  products: ProductRecord[] = [];
  pallets: PalletRecord[] = [];
  packageProducts: ProductRecord[] = [];
  selectedPallet: PalletRecord | null = null;
  scanLog: ScanLogEntry[] = [];
  asnDetectionBuffer: string[] = [];

  manualBarcode = '';
  asnEntry = '';
  trailerEntry = '';
  selectedClientUuid = '';
  selectedStoreNumber = '';
  palletForm = {
    height: null as number | null,
    length: null as number | null,
    width: null as number | null,
    weight: null as number | null,
  };
  damageBarcode = '';
  damageResolution: DamageResolution = 'DELIVERED AS IS';

  private readonly subscriptions = new Subscription();

  constructor(
    private api: BarcodeScannerApiService,
    private sessionService: BarcodeScannerSessionService,
    private scannerInput: ScannerInputService,
    private sound: BarcodeScannerSoundService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.sessionService.state$.subscribe((state) => {
        this.session = state;
        this.asnEntry = state.asnNumber;
        this.trailerEntry = state.trailerNumber;
        this.selectedClientUuid = state.poolPointClientUuid;
        this.selectedStoreNumber = state.storeNumber;
      })
    );
    this.subscriptions.add(
      this.scannerInput.statuses$.subscribe((statuses) => {
        this.statuses = statuses;
      })
    );
    this.subscriptions.add(
      this.scannerInput.scan$.subscribe((scan) => {
        this.handleScan(scan);
      })
    );

    this.loadContext();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  loadContext(): void {
    this.setLoading('Loading scanner context');
    this.api.getContext()
      .pipe(finalize(() => this.clearLoading()))
      .subscribe({
        next: ({ context, servicedStores, poolClients }) => {
          this.sessionService.applyContext(context, servicedStores);
          this.poolClients = poolClients;
          this.addLog('Scanner context loaded', 'info', 'setup', 'manual');
        },
        error: () => {
          this.toastr.error('Scanner context could not be loaded.');
          this.addLog('Scanner context failed to load', 'error', 'setup', 'manual');
        },
      });
  }

  unlockAudio(): void {
    this.sound.unlock();
    this.toastr.info('Scanner sounds are ready.');
  }

  selectClient(poolPointClientUuid: string): void {
    const client = this.poolClients.find((candidate) => candidate.pool_point_client_uuid === poolPointClientUuid);
    this.sessionService.patch({
      poolPointClientUuid,
      poolPointClientName: client ? this.clientLabel(client) : '',
    });
  }

  saveAsnAndTrailer(): void {
    this.sessionService.patch({
      asnNumber: this.asnEntry.trim(),
      trailerNumber: this.trailerEntry.trim(),
    });
    this.toastr.success('Scanner session updated.');
  }

  fetchTripForAsn(): void {
    const asnNumber = this.asnEntry.trim() || this.session.asnNumber;

    if (!asnNumber) {
      this.toastr.warning('Enter an ASN first.');
      return;
    }

    this.setLoading('Looking up trip');
    this.api.getTripByAsn(asnNumber, this.session.poolPointUuid)
      .pipe(finalize(() => this.clearLoading()))
      .subscribe({
        next: (trips) => {
          const trip = trips[0];

          if (trip) {
            this.applyTripLookup(trip, asnNumber);
            this.toastr.success('Trip loaded.');
            return;
          }

          this.generateTripNumber(asnNumber);
        },
        error: () => this.toastr.error('Trip lookup failed.'),
      });
  }

  generateBlindTrip(): void {
    this.setLoading('Generating blind trip');
    this.api.generateAsnAndTripNumber(this.asnEntry.trim(), this.session.poolPointUuid)
      .pipe(finalize(() => this.clearLoading()))
      .subscribe({
        next: (trip) => {
          this.applyTripLookup(trip, this.asnEntry.trim());
          this.toastr.success('Blind trip session is ready.');
          this.startTripAudit(true);
        },
        error: () => this.toastr.error('Blind trip could not be generated.'),
      });
  }

  startAsnDetection(): void {
    this.mode = 'asn-detect';
    this.asnDetectionBuffer = [];
    this.products = [];
    this.toastr.info('Scan any three cartons from the shipment.');
  }

  startTripAudit(blind: boolean): void {
    if (!this.session.tripListUuid && !this.session.tripNumber) {
      this.toastr.warning('Load or generate a trip first.');
      return;
    }

    this.mode = blind ? 'blind-trip-audit' : 'trip-audit';
    this.loadProductsForCurrentMode();
  }

  startStoreAudit(): void {
    if (!this.session.tripListUuid || !this.session.storeNumber) {
      this.toastr.warning('Select a trip and store first.');
      return;
    }

    this.mode = 'store-audit';
    this.loadProductsForCurrentMode();
  }

  loadStores(): void {
    if (!this.session.tripListUuid) {
      this.toastr.warning('Load a trip before selecting a store.');
      return;
    }

    this.setLoading('Loading stores');
    this.api.getStoreNumbers(this.session.tripListUuid, this.session.poolPointUuid)
      .pipe(finalize(() => this.clearLoading()))
      .subscribe({
        next: (stores) => {
          const serviced = new Set(this.session.servicedStores);
          this.storeOptions = serviced.size
            ? stores.filter((store) => serviced.has(String(store.store_number)))
            : stores;
          this.toastr.success('Stores loaded.');
        },
        error: () => this.toastr.error('Stores could not be loaded.'),
      });
  }

  selectStore(storeNumber: string): void {
    const store = this.storeOptions.find((candidate) => String(candidate.store_number) === String(storeNumber));
    this.sessionService.patch({
      storeNumber,
      shipmentListUuid: store?.shipment_list_uuid || this.session.shipmentListUuid,
      poolPointStoreUuid: store?.pool_point_store_uuid || this.session.poolPointStoreUuid,
    });
  }

  loadBarcodeList(): void {
    if (!this.session.tripNumber) {
      this.toastr.warning('Load a trip before viewing barcodes.');
      return;
    }

    this.mode = 'barcode-list';
    this.setLoading('Loading barcodes');
    this.api.getProducts({ trip_number: this.session.tripNumber })
      .pipe(finalize(() => this.clearLoading()))
      .subscribe({
        next: (products) => {
          this.products = products;
        },
        error: () => this.toastr.error('Barcode list could not be loaded.'),
      });
  }

  loadPallets(): void {
    if (!this.session.tripListUuid) {
      this.toastr.warning('Load a trip before viewing pallets.');
      return;
    }

    this.mode = 'pallet-list';
    this.setLoading('Loading pallets');
    this.api.getPalletList(this.session.tripListUuid, this.session.storeNumber)
      .pipe(finalize(() => this.clearLoading()))
      .subscribe({
        next: (pallets) => {
          this.pallets = pallets;
          this.selectedPallet = pallets[0] || null;

          if (this.selectedPallet) {
            this.loadPackageProducts(this.selectedPallet);
          }
        },
        error: () => this.toastr.error('Pallet list could not be loaded.'),
      });
  }

  loadPackageProducts(pallet: PalletRecord): void {
    const packageUuid = this.recordString(pallet, 'package_uuid');

    this.selectedPallet = pallet;
    this.mode = 'pallet-products';

    if (!packageUuid) {
      this.packageProducts = [];
      return;
    }

    this.setLoading('Loading pallet cartons');
    this.api.getProducts({ package_uuid: packageUuid })
      .pipe(finalize(() => this.clearLoading()))
      .subscribe({
        next: (products) => {
          this.packageProducts = products;
        },
        error: () => this.toastr.error('Pallet cartons could not be loaded.'),
      });
  }

  submitManualBarcode(): void {
    const barcode = this.manualBarcode.trim();

    if (!barcode) {
      return;
    }

    this.scannerInput.emitManualScan(barcode);
    this.manualBarcode = '';
  }

  createPallet(): void {
    if (!this.session.tripListUuid || !this.session.storeNumber) {
      this.toastr.warning('Select a trip and store before creating a pallet.');
      return;
    }

    const body = {
      trip_list_uuid: this.session.tripListUuid,
      load_pallets: 1,
      dimension_height: this.palletForm.height || 0,
      dimension_length: this.palletForm.length || 0,
      dimension_width: this.palletForm.width || 0,
      load_weight: this.palletForm.weight || 0,
      store_number: this.session.storeNumber,
      shipment_list_uuid: this.session.shipmentListUuid,
      pool_point_store_uuid: this.session.poolPointStoreUuid,
      pool_point_uuid: this.session.poolPointUuid,
      pool_point_client_uuid: this.session.poolPointClientUuid,
      build_date: this.todaySlashDate(),
      blind_trip_number: this.session.tripNumber,
    };

    this.setLoading('Creating pallet');
    this.api.createPallet(body)
      .pipe(finalize(() => this.clearLoading()))
      .subscribe({
        next: () => {
          this.toastr.success('Pallet created.');
          this.loadPallets();
        },
        error: () => this.toastr.error('Pallet could not be created.'),
      });
  }

  updatePalletCount(pallet: PalletRecord, cartonCount: string): void {
    const dimensionUuid = this.recordString(pallet, 'dimension_uuid');

    if (!dimensionUuid) {
      this.toastr.warning('This pallet does not have a dimension UUID.');
      return;
    }

    this.api.updatePackage(dimensionUuid, { carton_count: cartonCount })
      .subscribe({
        next: () => this.toastr.success('Pallet count updated.'),
        error: () => this.toastr.error('Pallet count could not be updated.'),
      });
  }

  removeProductFromPallet(product: ProductRecord): void {
    if (!this.selectedPallet) {
      return;
    }

    const productUuid = product.product_uuid || '';
    const packageUuid = this.recordString(this.selectedPallet, 'package_uuid');

    if (!productUuid || !packageUuid) {
      this.toastr.warning('Product or pallet UUID is missing.');
      return;
    }

    this.api.removeProductFromPackage({ product_uuid: productUuid, package_uuid: packageUuid })
      .subscribe({
        next: () => {
          this.toastr.success('Carton removed from pallet.');
          this.loadPackageProducts(this.selectedPallet!);
        },
        error: () => this.toastr.error('Carton could not be removed.'),
      });
  }

  submitDamage(): void {
    const barcode = this.damageBarcode.trim();

    if (!barcode) {
      this.toastr.warning('Enter or scan a damaged barcode first.');
      return;
    }

    const resolution = this.damageResolution;
    const returnStatus = resolution === 'HELD FOR DISPOSITION' ? 'OUT OF MARKET' : 'DAMAGED';
    const body = this.scanBody(barcode, this.session.storeNumber, {
      audited: this.isDeliveredResolution(resolution) ? 'true' : 'false',
      received_in: this.isDeliveredResolution(resolution) ? 'true' : 'false',
      resolution,
      return_status: returnStatus,
    });

    this.setLoading('Submitting damage report');
    this.api.getProducts({ barcode, trip_number: this.session.tripNumber })
      .subscribe({
        next: (products) => {
          const product = products[0];
          const request = product?.product_uuid
            ? this.api.updateProduct(product.product_uuid, body)
            : this.api.createProduct(body);

          request
            .pipe(finalize(() => this.clearLoading()))
            .subscribe({
              next: () => {
                this.toastr.success('Damage report submitted.');
                this.damageBarcode = '';
                this.loadProductsForCurrentMode();
              },
              error: () => this.toastr.error('Damage report could not be submitted.'),
            });
        },
        error: () => {
          this.clearLoading();
          this.toastr.error('Damage barcode lookup failed.');
        },
      });
  }

  setDamageBarcode(barcode: string): void {
    this.damageBarcode = barcode;
  }

  probeAdapter(source: 'webhid' | 'webusb' | 'webserial'): void {
    void this.scannerInput.probeExperimentalAdapter(source);
  }

  clientLabel(client: PoolClient): string {
    return String(client.client_name || client.pool_point_client_name || client.company_name || client.name || client.pool_point_client_uuid);
  }

  recordString(record: ProductRecord | PalletRecord | StoreOption | null, key: string): string {
    if (!record) {
      return '';
    }

    const value = record[key];
    return value === null || value === undefined ? '' : String(value);
  }

  productStatus(product: ProductRecord): string {
    if (this.api.normalizeBoolean(product.audited)) {
      return 'Audited';
    }

    if (this.api.normalizeBoolean(product.received_in)) {
      return 'Received';
    }

    return 'Open';
  }

  private handleScan(scan: ScanEvent): void {
    this.sound.unlock();

    if (this.mode === 'asn-detect' || (!this.session.asnNumber && this.mode === 'setup')) {
      this.collectAsnBarcode(scan);
      return;
    }

    if (this.processingBarcodes.has(scan.value)) {
      this.addLog(scan.value, 'duplicate', this.mode, scan.source, 'Already processing this barcode.');
      return;
    }

    switch (this.mode) {
      case 'trip-audit':
        this.handleNormalTripScan(scan);
        break;
      case 'blind-trip-audit':
        this.handleBlindTripScan(scan);
        break;
      case 'store-audit':
        this.handleStoreAuditScan(scan);
        break;
      case 'pallet-products':
        this.handlePalletProductScan(scan);
        break;
      default:
        this.addLog(scan.value, 'info', this.mode, scan.source, 'Scan captured. Choose a workflow to process it.');
        this.toastr.info('Scan captured. Choose a workflow to process it.');
        break;
    }
  }

  private collectAsnBarcode(scan: ScanEvent): void {
    if (!this.asnDetectionBuffer.includes(scan.value)) {
      this.asnDetectionBuffer = [...this.asnDetectionBuffer, scan.value].slice(-3);
    }

    this.addLog(scan.value, 'info', 'asn-detect', scan.source, `${this.asnDetectionBuffer.length}/3 ASN lookup scans captured.`);

    if (this.asnDetectionBuffer.length < 3) {
      return;
    }

    this.setLoading('Finding ASN from scans');
    this.api.fetchAsnFromBarcodes(this.asnDetectionBuffer)
      .pipe(finalize(() => this.clearLoading()))
      .subscribe({
        next: (response) => {
          if (this.api.responseMessage(response).toUpperCase() !== 'OK') {
            this.sound.play('warning');
            this.toastr.warning('ASN was not found from these barcodes.');
            return;
          }

          this.applyTripLookup(response, response.asn_number || '');
          this.asnDetectionBuffer = [];
          this.mode = 'setup';
          this.sound.play('success');
          this.toastr.success('ASN found.');
        },
        error: () => {
          this.sound.play('error');
          this.toastr.error('ASN lookup failed.');
        },
      });
  }

  private handleNormalTripScan(scan: ScanEvent): void {
    this.processingBarcodes.add(scan.value);
    const storePrefix = scan.value.slice(0, 5);
    const storeNumber = this.api.extractStoreNumber(scan.value);
    const body = this.scanBody(scan.value, storeNumber, { received_in: 't' });

    if (this.session.servicedStores.length > 0 && !this.session.servicedStores.includes(storePrefix)) {
      this.api.updateReturnStatus({
        ...body,
        store_number: '',
        return_status: 'HELD FOR DISPOSITION',
      })
        .pipe(finalize(() => this.processingBarcodes.delete(scan.value)))
        .subscribe({
          next: () => this.finishScan(scan, 'warning', 'Store mismatch. Barcode held for disposition.'),
          error: () => this.finishScan(scan, 'error', 'Failed to mark barcode held for disposition.'),
        });
      return;
    }

    this.api.handleBarcodeScan(body)
      .pipe(finalize(() => this.processingBarcodes.delete(scan.value)))
      .subscribe({
        next: (response) => {
          const status = this.api.responseMessage(response).toLowerCase();

          if (status === 'updated') {
            this.products = this.products.filter((product) => product.barcode !== scan.value);
            this.finishScan(scan, 'success', 'Barcode received into trip.');
            return;
          }

          if (status === 'duplicate') {
            this.finishScan(scan, 'duplicate', 'Duplicate barcode detected.');

            if (window.confirm('Duplicate barcode detected. Add it as an overage barcode?')) {
              this.insertDuplicate(scan.value, storeNumber);
            }
            return;
          }

          if (status === 'not_in_list') {
            this.finishScan(scan, 'warning', 'Barcode was not in the trip list.');
            return;
          }

          this.finishScan(scan, 'info', `Trip scan response: ${status}`);
        },
        error: () => this.finishScan(scan, 'error', 'Trip scan failed.'),
      });
  }

  private handleBlindTripScan(scan: ScanEvent): void {
    this.processingBarcodes.add(scan.value);
    const storePrefix = scan.value.slice(0, 5);
    const storeNumber = this.api.extractStoreNumber(scan.value);
    const body = this.scanBody(scan.value, storeNumber, {
      blind_trip_number: this.session.tripNumber,
      received_in: 'true',
      audited: 'false',
    });

    if (this.session.servicedStores.length > 0 && !this.session.servicedStores.includes(storePrefix)) {
      this.api.updateReturnStatus({
        ...body,
        return_status: 'HELD FOR DISPOSITION',
      }, 'blind')
        .pipe(finalize(() => this.processingBarcodes.delete(scan.value)))
        .subscribe({
          next: () => this.finishScan(scan, 'warning', 'Store mismatch. Blind scan held for disposition.'),
          error: () => this.finishScan(scan, 'error', 'Failed to mark blind scan held for disposition.'),
        });
      return;
    }

    this.api.getProducts({ barcode: scan.value, trip_number: this.session.tripNumber })
      .subscribe({
        next: (matches) => {
          if (matches.length > 0 && !window.confirm('Duplicate barcode detected. Add it to this blind trip anyway?')) {
            this.processingBarcodes.delete(scan.value);
            this.finishScan(scan, 'duplicate', 'Duplicate blind-trip barcode skipped.');
            return;
          }

          this.api.createProduct(body)
            .pipe(finalize(() => this.processingBarcodes.delete(scan.value)))
            .subscribe({
              next: () => {
                this.products = [body as ProductRecord, ...this.products];
                this.finishScan(scan, matches.length > 0 ? 'duplicate' : 'success', 'Blind-trip barcode added.');
              },
              error: () => this.finishScan(scan, 'error', 'Blind-trip barcode could not be added.'),
            });
        },
        error: () => {
          this.processingBarcodes.delete(scan.value);
          this.finishScan(scan, 'error', 'Blind-trip duplicate check failed.');
        },
      });
  }

  private handleStoreAuditScan(scan: ScanEvent): void {
    this.processingBarcodes.add(scan.value);
    this.api.getProducts({ barcode: scan.value, trip_number: this.session.tripNumber })
      .subscribe({
        next: (matches) => {
          const alreadyAudited = matches.find((product) => this.api.normalizeBoolean(product.audited));

          if (alreadyAudited) {
            this.processingBarcodes.delete(scan.value);
            this.finishScan(scan, 'duplicate', 'Barcode was already audited.');
            return;
          }

          const sameStoreProduct = matches.find((product) => String(product.store_number || '') === this.session.storeNumber);

          if (!sameStoreProduct?.product_uuid) {
            this.addStoreAuditOverage(scan, matches.length > 0);
            return;
          }

          this.api.updateProduct(sameStoreProduct.product_uuid, { audited: 'true' })
            .pipe(finalize(() => this.processingBarcodes.delete(scan.value)))
            .subscribe({
              next: () => {
                this.products = this.products.filter((product) => product.product_uuid !== sameStoreProduct.product_uuid);
                this.finishScan(scan, 'success', 'Barcode audited.');
              },
              error: () => this.finishScan(scan, 'error', 'Barcode audit failed.'),
            });
        },
        error: () => {
          this.processingBarcodes.delete(scan.value);
          this.finishScan(scan, 'error', 'Store-audit barcode lookup failed.');
        },
      });
  }

  private handlePalletProductScan(scan: ScanEvent): void {
    if (!this.selectedPallet) {
      this.toastr.warning('Select a pallet first.');
      return;
    }

    const packageUuid = this.recordString(this.selectedPallet, 'package_uuid');
    const palletStoreNumber = this.recordString(this.selectedPallet, 'store_number') || this.session.storeNumber;

    if (!packageUuid) {
      this.toastr.warning('Selected pallet is missing a package UUID.');
      return;
    }

    if (this.packageProducts.some((product) => product.barcode === scan.value)) {
      this.finishScan(scan, 'duplicate', 'Barcode is already on this pallet.');
      return;
    }

    this.processingBarcodes.add(scan.value);
    this.api.addCartonToPallet({
      trip_number: this.session.tripNumber,
      package_uuid: packageUuid,
      store_number: palletStoreNumber,
      barcode: scan.value,
      trip_list_uuid: this.session.tripListUuid,
      asn_number: this.session.asnNumber,
      pool_point_uuid: this.session.poolPointUuid,
      pool_point_client_uuid: this.session.poolPointClientUuid,
      pool_point_store_uuid: this.session.poolPointStoreUuid,
      date: this.todayDate(),
    })
      .pipe(finalize(() => this.processingBarcodes.delete(scan.value)))
      .subscribe({
        next: () => {
          this.finishScan(scan, 'success', 'Carton added to pallet.');
          this.loadPackageProducts(this.selectedPallet!);
        },
        error: () => this.finishScan(scan, 'error', 'Carton could not be added to pallet.'),
      });
  }

  private addStoreAuditOverage(scan: ScanEvent, storeMismatch: boolean): void {
    const message = storeMismatch
      ? `Barcode belongs to another store. Add it as an overage for store ${this.session.storeNumber}?`
      : `Barcode is not in this trip. Add it as an overage for store ${this.session.storeNumber}?`;

    if (!window.confirm(message)) {
      this.processingBarcodes.delete(scan.value);
      this.finishScan(scan, 'warning', 'Store-audit overage skipped.');
      return;
    }

    const body = this.scanBody(scan.value, this.session.storeNumber, {
      received_in: 'true',
      audited: 'true',
      return_status: 'OVERAGE',
    });

    this.api.createProduct(body)
      .pipe(finalize(() => this.processingBarcodes.delete(scan.value)))
      .subscribe({
        next: () => this.finishScan(scan, 'warning', 'Store-audit overage added.'),
        error: () => this.finishScan(scan, 'error', 'Store-audit overage could not be added.'),
      });
  }

  private insertDuplicate(barcode: string, storeNumber: string): void {
    this.api.insertOverageBarcode(this.scanBody(barcode, storeNumber, {
      received_in: 't',
      return_status: 'OVERAGE',
    }))
      .subscribe({
        next: () => this.toastr.success('Duplicate overage barcode added.'),
        error: () => this.toastr.error('Duplicate overage could not be added.'),
      });
  }

  private loadProductsForCurrentMode(): void {
    if (this.mode === 'trip-audit') {
      this.setLoading('Loading trip barcodes');
      this.api.getProducts({ asn_number: this.session.asnNumber, received_in: false })
        .pipe(finalize(() => this.clearLoading()))
        .subscribe({
          next: (products) => {
            this.products = products;
          },
          error: () => this.toastr.error('Trip barcodes could not be loaded.'),
        });
      return;
    }

    if (this.mode === 'blind-trip-audit') {
      this.setLoading('Loading blind trip barcodes');
      this.api.getProducts({ trip_list_uuid: this.session.tripListUuid })
        .pipe(finalize(() => this.clearLoading()))
        .subscribe({
          next: (products) => {
            this.products = products;
          },
          error: () => this.toastr.error('Blind trip barcodes could not be loaded.'),
        });
      return;
    }

    if (this.mode === 'store-audit') {
      this.setLoading('Loading store audit barcodes');
      this.api.getProducts({
        trip_list_uuid: this.session.tripListUuid,
        audited: false,
        store_number: this.session.storeNumber,
      })
        .pipe(finalize(() => this.clearLoading()))
        .subscribe({
          next: (products) => {
            this.products = products;
          },
          error: () => this.toastr.error('Store audit barcodes could not be loaded.'),
        });
    }
  }

  private generateTripNumber(asnNumber: string): void {
    this.setLoading('Generating trip number');
    this.api.generateTripNumber(asnNumber, this.session.poolPointUuid)
      .pipe(finalize(() => this.clearLoading()))
      .subscribe({
        next: (trip) => {
          this.applyTripLookup(trip, asnNumber);
          this.toastr.success('Trip number generated.');
        },
        error: () => this.toastr.error('Trip number could not be generated.'),
      });
  }

  private applyTripLookup(trip: TripLookup, fallbackAsn: string): void {
    const asnNumber = trip.asn_number || fallbackAsn || this.session.asnNumber;
    const tripNumber = trip.trip_number || trip.blind_trip_number || this.session.tripNumber;

    this.sessionService.patch({
      asnNumber,
      shipmentListUuid: trip.shipment_list_uuid || this.session.shipmentListUuid,
      tripNumber,
      tripListUuid: trip.trip_list_uuid || this.session.tripListUuid,
    });
  }

  private scanBody(barcode: string, storeNumber: string, extra: Record<string, unknown> = {}): Record<string, unknown> {
    return {
      domain_uuid: this.session.domainUuid,
      trip_number: this.session.tripNumber,
      asn_number: this.session.asnNumber,
      store_number: storeNumber,
      trip_list_uuid: this.session.tripListUuid,
      trailer_id: this.session.trailerNumber,
      date: this.todayDate(),
      barcode,
      pool_point_uuid: this.session.poolPointUuid,
      pool_point_client_uuid: this.session.poolPointClientUuid,
      ...extra,
    };
  }

  private isDeliveredResolution(resolution: DamageResolution): boolean {
    return [
      'DELIVERED AS IS',
      'RETAPED AND DELIVERED',
      'REPACKAGED AND DELIVERED',
      'BARCODE ILLEGIBLE',
      'LABEL MISSING',
    ].includes(resolution);
  }

  private finishScan(scan: ScanEvent, outcome: ScanOutcome, message: string): void {
    this.sound.play(outcome);
    this.addLog(scan.value, outcome, this.mode, scan.source, message);

    if (outcome === 'success') {
      this.toastr.success(message);
      return;
    }

    if (outcome === 'error') {
      this.toastr.error(message);
      return;
    }

    if (outcome === 'warning' || outcome === 'duplicate') {
      this.toastr.warning(message);
      return;
    }

    this.toastr.info(message);
  }

  private addLog(
    barcode: string,
    outcome: ScanOutcome,
    mode: ScannerMode,
    source: ScanEvent['source'],
    message = barcode
  ): void {
    this.scanLog = [
      {
        barcode,
        source,
        mode,
        outcome,
        message,
        timestamp: new Date(),
      },
      ...this.scanLog,
    ].slice(0, 50);
  }

  private setLoading(label: string): void {
    this.loading = true;
    this.loadingLabel = label;
  }

  private clearLoading(): void {
    this.loading = false;
    this.loadingLabel = '';
  }

  private todayDate(): string {
    return new Date().toISOString().slice(0, 10);
  }

  private todaySlashDate(): string {
    const date = new Date();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}/${month}/${day}`;
  }
}
